/**
 * AI-generated candidate list for deployment wizard Step 3.
 */
import type { DeploymentCandidate } from '../../types/deploy'

interface CandidatesListProps {
  candidates: DeploymentCandidate[]
  selectedSymbols: string[]
  onToggle: (symbol: string) => void
  isLoading: boolean
  error?: string | null
}

export function CandidatesList({
  candidates,
  selectedSymbols,
  onToggle,
  isLoading,
  error,
}: CandidatesListProps) {
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent mb-4" />
        <p className="text-gray-500">AI is analyzing your thesis and generating candidates...</p>
        <p className="text-sm text-gray-400 mt-1">This may take a few seconds</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
          <ExclamationIcon className="w-6 h-6 text-red-600" />
        </div>
        <p className="text-red-600 font-medium">Failed to generate suggestions</p>
        <p className="text-sm text-gray-500 mt-1">{error}</p>
      </div>
    )
  }

  if (candidates.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No candidates generated. Try adjusting your thesis narrative.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-500">
          Select candidates to deploy. Click to toggle selection.
        </p>
        <span className="text-sm font-medium text-gray-700">
          {selectedSymbols.length} of {candidates.length} selected
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {candidates.map((candidate) => {
          const isSelected = selectedSymbols.includes(candidate.symbol)

          return (
            <button
              key={candidate.symbol}
              onClick={() => onToggle(candidate.symbol)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                isSelected
                  ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="text-lg font-bold text-gray-900">
                    {candidate.symbol}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">{candidate.name}</span>
                </div>
                <ConfidenceBadge confidence={candidate.confidence} />
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {candidate.rationale}
              </p>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">
                  Weight: <span className="font-medium text-gray-900">{candidate.suggested_weight}%</span>
                </span>
                <span className="text-gray-500">
                  Amount:{' '}
                  <span className="font-medium text-gray-900">
                    ${candidate.suggested_amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </span>
                </span>
              </div>

              {/* Selection indicator */}
              <div
                className={`mt-3 pt-3 border-t ${
                  isSelected ? 'border-primary-200' : 'border-gray-100'
                } flex items-center justify-center`}
              >
                <span
                  className={`text-sm font-medium ${
                    isSelected ? 'text-primary-600' : 'text-gray-400'
                  }`}
                >
                  {isSelected ? '✓ Selected' : 'Click to select'}
                </span>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ConfidenceBadge({ confidence }: { confidence: 'high' | 'medium' | 'low' }) {
  const styles = {
    high: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-gray-100 text-gray-800',
  }

  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[confidence]}`}
    >
      {confidence}
    </span>
  )
}

function ExclamationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  )
}
